import { deleteJSON, get, postJSON, putJSON } from '../../../utils/Request';

export type QuotaPolicyOptions = {
  policyType?: string;
  periods?: string[];
  description?: string;
  independentPeriods?: string[];
};

export type PlanFeature = {
  featureNo?: string;
  featureCode?: string;
  displayName?: string;
  status?: string;
  quotaPolicyOptions?: QuotaPolicyOptions | null;
};

/** GET /api/v1/plan-features 的 content。 */
export type PlanFeaturePage = {
  records?: PlanFeature[];
  total?: number;
  pageNum?: number;
  pageSize?: number;
};

export type ApiResource = {
  resourceCode?: string;
  httpMethod?: string;
  pathPattern?: string;
  securityType?: string;
  status?: string;
};

/** GET /api/v1/permission-apis 的 content。 */
export type ApiResourcePage = {
  records?: ApiResource[];
  total?: number;
  pageNum?: number;
  pageSize?: number;
};

export type FeatureDetail = {
  featureNo?: string;
  featureCode?: string;
  displayName?: string;
  domainCode?: string;
  status?: string;
  apis?: ApiResource[];
  quotaPolicyOptions?: QuotaPolicyOptions | null;
};

export type FeatureDetailContent = {
  data?: FeatureDetail;
  configHash?: string;
};

export type CreatePlanFeatureBody = {
  resourceCode: string;
  featureCode: string;
  displayName: string;
  domainCode?: string;
};

/** 查询 API 权限点详情。返回体原样交给页面。 */
export function getPlanFeatureDetail(featureNo: string) {
  return get<FeatureDetailContent>({
    path: `/api/v1/plan-features/${encodeURIComponent(featureNo)}`
  });
}

/** 分页查询可绑定的套餐 API。返回体原样交给页面。 */
export function getPermissionApis(query: { pageNum: number; pageSize: number }) {
  return get<ApiResourcePage>({
    path: '/api/v1/permission-apis',
    params: {
      pageNum: query.pageNum,
      pageSize: query.pageSize
    }
  });
}

export type UpdatePlanFeatureBody = {
  displayName: string;
  status: 'ACTIVE' | 'DISABLED';
  expectedConfigHash: string;
};

/** 删除未被引用的权限点。expectedConfigHash 原样传回详情里的 configHash。被引用或配置变化时返回 409。 */
export function deletePlanFeature(featureNo: string, expectedConfigHash: string) {
  return deleteJSON<null>({
    path: `/api/v1/plan-features/${encodeURIComponent(featureNo)}`,
    params: { expectedConfigHash }
  });
}

/** 修改权限点展示名称和启停状态。expectedConfigHash 原样传回详情里的 configHash。返回体原样交给页面。 */
export function updatePlanFeature(featureNo: string, body: UpdatePlanFeatureBody) {
  return putJSON<FeatureDetailContent>({
    path: `/api/v1/plan-features/${encodeURIComponent(featureNo)}`,
    params: body
  });
}

/** 新增 API 权限点并绑定。返回体原样交给页面。 */
export function createPlanFeature(body: CreatePlanFeatureBody) {
  return postJSON<FeatureDetailContent>({
    path: '/api/v1/plan-features',
    params: body
  });
}

/** 分页查询最小权限单元。displayName 可选，包含匹配；去首尾空格，空白不传。返回体原样交给页面。 */
export function getPlanFeatures(query: { pageNum: number; pageSize: number; displayName?: string }) {
  const displayName = query.displayName?.trim() ?? '';
  return get<PlanFeaturePage>({
    path: '/api/v1/plan-features',
    params: {
      pageNum: query.pageNum,
      pageSize: query.pageSize,
      ...(displayName ? { displayName } : {})
    }
  });
}

export type ResourceType = 'api' | 'menu';

export type AdminPermission = {
  id: string;
  name: string;
  resourceType: ResourceType;
  resourceValue: string;
  description: string;
  enabled: boolean;
  createdAt: string;
};

export type PermissionFormValues = {
  name: string;
  resourceType: ResourceType;
  resourceValue: string;
  description: string;
  enabled: boolean;
};

export type PermissionGroupFormValues = {
  name: string;
  permissionIds: string[];
};

export const RESOURCE_TYPE_LABEL: Record<ResourceType, string> = {
  api: 'API',
  menu: '菜单MENU'
};

export const RESOURCE_TYPE_OPTIONS: { value: ResourceType; label: string }[] = [
  { value: 'api', label: 'API' },
  { value: 'menu', label: '菜单MENU' }
];

export const adminPermissionList: AdminPermission[] = [
  {
    id: 'p-1',
    name: 'Tiktok类目查询',
    resourceType: 'api',
    resourceValue: '/api/v1/tiktok/category/queryAll',
    description: 'Tiktok类目查询',
    enabled: true,
    createdAt: ''
  },
  {
    id: 'p-2',
    name: 'tiktok 达人-查询达人列表',
    resourceType: 'api',
    resourceValue: '/api/v1/tiktok/influencer/query',
    description: '查询达人列表',
    enabled: true,
    createdAt: ''
  },
  {
    id: 'p-3',
    name: 'tiktok 达人-查询达人详情',
    resourceType: 'api',
    resourceValue: '/api/v1/tiktok/influencer/detail/{influencerId}/{siteId}',
    description: '查询达人详情',
    enabled: true,
    createdAt: ''
  },
  {
    id: 'p-4',
    name: 'tiktok 达人-查询达人带货商品列表',
    resourceType: 'api',
    resourceValue: '/api/v1/tiktok/influencer/product/query',
    description: '查询达人带货商品列表',
    enabled: true,
    createdAt: ''
  },
  {
    id: 'p-5',
    name: 'tiktok 达人-查询达人视频列表',
    resourceType: 'api',
    resourceValue: '/api/v1/tiktok/influencer/video/query',
    description: '查询达人视频列表',
    enabled: true,
    createdAt: ''
  },
  {
    id: 'p-6',
    name: 'tiktok 达人-查询达人直播列表',
    resourceType: 'api',
    resourceValue: '/api/v1/tiktok/influencer/live/query',
    description: '查询达人直播列表',
    enabled: true,
    createdAt: ''
  },
  {
    id: 'p-7',
    name: 'tiktok 达人-查询达人粉丝画像',
    resourceType: 'api',
    resourceValue: '/api/v1/tiktok/influencer/fansPortrait/{influencerId}/{siteId}',
    description: '查询达人粉丝画像',
    enabled: true,
    createdAt: ''
  },
  {
    id: 'p-8',
    name: 'tiktok 商品-查询商品列表',
    resourceType: 'api',
    resourceValue: '/api/v1/tiktok/product/query',
    description: '查询商品列表',
    enabled: true,
    createdAt: ''
  },
  {
    id: 'p-9',
    name: 'tiktok 商品-查询商品详情',
    resourceType: 'api',
    resourceValue: '/api/v1/tiktok/product/detail/{productId}/{siteId}',
    description: '查询商品详情',
    enabled: true,
    createdAt: ''
  },
  {
    id: 'p-10',
    name: 'tiktok 商品-查询商品销售达人列表',
    resourceType: 'api',
    resourceValue: '/api/v1/tiktok/product/influencer/query',
    description: '查询商品销售达人列表',
    enabled: true,
    createdAt: ''
  },
  {
    id: 'p-11',
    name: '工作台菜单',
    resourceType: 'menu',
    resourceValue: '/creator',
    description: '商家工作台入口',
    enabled: true,
    createdAt: '2026-03-01 10:00:00'
  },
  {
    id: 'p-12',
    name: '达人广场菜单',
    resourceType: 'menu',
    resourceValue: '/creator/talent_square',
    description: '达人广场页面',
    enabled: false,
    createdAt: '2026-03-02 11:20:00'
  }
];
